import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosDocumentProcessing from '../../services/utility/axios-document-processing';
import moment from 'moment';

// ✅ CHANGE: Added validationErrors to initial state
const initialState = {
    document: null,
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    lastAction: null, // Tracks the last successful action ('save', 'process', 'reject') for UI feedback
    error: null,
    validationErrors: null // ← NEW: For client-side validation errors
};

/**
 * Fetches a single document by its ID. This is the entry point when the modal is opened.
 */
export const fetchDocumentById = createAsyncThunk(
    'documentProcessing/fetchById',
    async (documentId, { rejectWithValue }) => {
        try {
            const response = await axiosDocumentProcessing.get(`/invoice/${documentId}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch document data.');
        }
    }
);

/**
 * Saves the modified document. It transforms the data into the required API payload format before sending.
 */
export const saveDocument = createAsyncThunk(
    'documentProcessing/saveDocument',
    async (documentData, { rejectWithValue }) => {
        try {
            // Create a deep copy to avoid direct state mutation during formatting.
            const payload = JSON.parse(JSON.stringify(documentData));

            delete payload.file_urls;

            // --- Format the payload to match API requirements ---
            
            // Format dates to "YYYY-MM-DD HH:mm:ss" string format.
            if (payload.invoice_date) {
                payload.invoice_date = moment(payload.invoice_date).format('YYYY-MM-DD HH:mm:ss');
            }
            // payload.due_date = payload.due_date ? moment(payload.due_date).format('YYYY-MM-DD HH:mm:ss') : null;

            // Format numeric fields in invoice lines to strings with 4 decimal places.
            payload.invoice_lines = payload.invoice_lines.map(line => ({
                ...line,
                quantity: parseFloat(line.quantity || 0).toFixed(4),
                unit_price: parseFloat(line.unit_price || 0).toFixed(4),
                line_total: parseFloat(line.line_total || 0).toFixed(4),
            }));
            
            // Make the live PATCH request.
            const response = await axiosDocumentProcessing.patch(`/invoice/${payload.id}`, payload);
            
            // Return response and an action identifier for the UI.
            return { ...response.data, action: 'save' };

        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to save the document.');
        }
    }
);

/**
 * Processes the document. (Placeholder for the real API call)
 */
// export const processDocument = createAsyncThunk(
//     'documentProcessing/processDocument',
//     async (documentData, { rejectWithValue }) => {
//         try {
//             // TODO: Replace with the actual API call, e.g.:
//             // const response = await axiosDocumentProcessing.post(`/invoice/${documentData.id}/process`);
//             // Simulating a successful response for now:
//             const response = await new Promise(resolve => setTimeout(() => resolve({ data: documentData }), 1000));
//             return { ...response.data, action: 'process' };
//         } catch (error) {
//             return rejectWithValue(error.response?.data?.message || 'Failed to process document.');
//         }
//     }
// );
export const processDocument = createAsyncThunk(
    'documentProcessing/processDocument',
    async (documentData, { rejectWithValue }) => {
        try {
            // Create a deep copy to avoid direct state mutation during formatting
            const payload = JSON.parse(JSON.stringify(documentData));

            // Format dates to "YYYY-MM-DD HH:mm:ss" string format
            if (payload.invoice_date) {
                payload.invoice_date = moment(payload.invoice_date).format('YYYY-MM-DD HH:mm:ss');
            }
            // payload.due_date = payload.due_date ? moment(payload.due_date).format('YYYY-MM-DD HH:mm:ss') : null;

            // Format numeric fields in invoice lines to strings with 4 decimal places
            payload.invoice_lines = payload.invoice_lines.map(line => ({
                ...line,
                quantity: parseFloat(line.quantity || 0).toFixed(4),
                unit_price: parseFloat(line.unit_price || 0).toFixed(4),
                line_total: parseFloat(line.line_total || 0).toFixed(4),
                discount: line.discount || null,
            }));

            // Format amount fields to ensure they're strings with proper decimal places
            if (payload.amount) {
                payload.amount = {
                    ...payload.amount,
                    subtotal: parseFloat(payload.amount.subtotal || 0).toFixed(4),
                    discount: payload.amount.discount || "0",
                    tax: payload.amount.tax || "0",
                    vat: parseFloat(payload.amount.vat || 0).toFixed(4),
                    shipping: payload.amount.shipping || "0",
                    amount_paid: payload.amount.amount_paid || "0",
                    due_amount: parseFloat(payload.amount.due_amount || 0).toFixed(4),
                    total_including_vat_tax_discounts: parseFloat(payload.amount.total_including_vat_tax_discounts || 0).toFixed(4)
                };
            }

            // Ensure required fields exist
            if (!payload.additional_fields) {
                payload.additional_fields = [];
            }
            if (payload.archived === undefined) {
                payload.archived = false;
            }

            // Make the actual API call
            const response = await axiosDocumentProcessing.post(`/invoice/validate/${payload.id}`, payload);
            
            return { ...response.data, action: 'process' };

        } catch (error) {
            return rejectWithValue(error.response?.data?.errors?.[0] || 'Failed to process document.');
        }
    }
);

/**
 * Rejects the document with remarks
 */
export const rejectDocument = createAsyncThunk(
    'documentProcessing/rejectDocument',
    async ({ documentId, remarks }, { rejectWithValue }) => {
        try {
            const payload = {
                // invoiceId: documentId,
                status: 'rejected',
                remarks
            };
            
            const response = await axiosDocumentProcessing.patch(`/invoice/${documentId}/status`, payload);
            return { ...response.data, action: 'reject' };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to reject document.');
        }
    }
);

export const archiveDocument = createAsyncThunk(
    'documentProcessing/archiveDocument',
    async (documentId, { rejectWithValue }) => {
        try {
            const response = await axiosDocumentProcessing.patch(`/invoice/archive/${documentId}`);
            return { ...response.data, action: 'archive' };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to archive document.');
        }
    }
);

/**
 * Deletes a line item from the server (for API-populated items)
 */
export const deleteLineItemFromServer = createAsyncThunk(
    'documentProcessing/deleteLineItemFromServer',
    async ({ invoiceId, lineItemId, index }, { rejectWithValue }) => {
        try {
            await axiosDocumentProcessing.delete(`/invoice/${invoiceId}/invoicelines/${lineItemId}`);
            return { index, action: 'delete_line_item' };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete line item.');
        }
    }
);

const documentProcessingSlice = createSlice({
    name: 'documentProcessing',
    initialState,
    /**
     * Synchronous reducers for direct state manipulation from the component.
     */
    reducers: {
        updateDocumentField: (state, action) => {
            const { section, key, value } = action.payload;
            if (state.document) {
                if (section) {
                    state.document[section][key] = value;
                } else {
                    state.document[key] = value;
                }
            }
        },
        updateLineItem: (state, action) => {
            const { index, key, value } = action.payload;
            if (state.document && state.document.invoice_lines[index]) {
                state.document.invoice_lines[index][key] = value;
            }
        },
        addLineItem: (state) => {
            if (state.document) {
                const existing = state.document.invoice_lines || [];
                const nextLineNumber = existing.reduce((max, l) => Math.max(max, Number(l.line_number) || 0), 0) + 1;
                state.document.invoice_lines.push({
                    id: `new_${Date.now()}`, // Temporary client-side ID
                    line_number: nextLineNumber,
                    quantity: 1,
                    unit_price: 0,
                    line_total: 0,
                    vat_percent: 0,
                    vat_value: 0,
                    product: null,
                    product_name: '',
                    net_total: 0,
                    supplier_product_code: '',
                    discount: null,
                    line_vat_code: "0",
                    vat_code: "0",
                    // description: ''
                });
            }
        },
        removeLineItem: (state, action) => {
             const { index } = action.payload;
            if (state.document) {
                state.document.invoice_lines.splice(index, 1);
            }
        },
        // ✅ NEW: Validation error management reducers
        setValidationErrors: (state, action) => {
            state.validationErrors = action.payload;
        },
        clearValidationErrors: (state) => {
            state.validationErrors = null;
        },
        clearErrors: (state) => {
            state.error = null;
        },
        // Resets the entire slice to its initial state, used when closing the modal.
        clearDocument: () => initialState
    },
    /**
     * Handles state changes for the asynchronous thunks.
     */
    extraReducers: (builder) => {
        const handlePending = (state) => {
            state.status = 'loading';
            state.error = null;
        };
        const handleRejected = (state, action) => {
            state.status = 'failed';
            state.error = action.payload;
        };

        builder
            // --- Fetch Document Logic ---
            .addCase(fetchDocumentById.pending, handlePending)
            .addCase(fetchDocumentById.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.document = action.payload; // Correctly assigns the fetched data.
                state.lastAction = null;
                state.validationErrors = null; // ✅ NEW: Clear validation errors on fetch
            })
            .addCase(fetchDocumentById.rejected, handleRejected)
            .addCase(deleteLineItemFromServer.fulfilled, (state, action) => {
                const { index } = action.payload;
                if (state.document && state.document.invoice_lines[index]) {
                    state.document.invoice_lines.splice(index, 1);
                }
                state.status = 'succeeded';
            })
            .addCase(deleteLineItemFromServer.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })

            // --- Save, Process, and Reject Logic ---
            // These actions share a similar fulfilled state update pattern.
            .addMatcher(
                (action) => [saveDocument.fulfilled.type, processDocument.fulfilled.type, rejectDocument.fulfilled.type, archiveDocument.fulfilled.type].includes(action.type),
                (state, action) => {
                    state.status = 'succeeded';
                    state.lastAction = action.payload.action; // Sets the specific action for UI feedback.
                    state.validationErrors = null; // ✅ NEW: Clear validation errors on successful action
                }
            )
            // Add a matcher for all pending actions to reduce boilerplate
            .addMatcher(
                (action) => action.type.endsWith('/pending'),
                handlePending
            )
            // Add a matcher for all rejected actions
            .addMatcher(
                (action) => action.type.endsWith('/rejected'),
                handleRejected
            );
    }
});

// ✅ CHANGE: Added validation actions to exports
export const { 
    updateDocumentField, 
    updateLineItem, 
    addLineItem,
    removeLineItem,
    setValidationErrors,
    clearValidationErrors,
    clearErrors,
    clearDocument 
} = documentProcessingSlice.actions;

export default documentProcessingSlice.reducer;